import React, { useCallback, useMemo, useState } from 'react'
import { Grid } from '@mui/material'
import { Datagrid } from '@refera/ui-web'

import useStyles from './styles'
import moment from 'moment'

const TABLE_SX = {
  fontSize: '1.4rem',
  width: '100%',
  '& .MuiDataGrid-cell:hover': {
    cursor: 'pointer',
  },
}

const dateFormats = ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm', 'DD/MM/YYYY']

export default function ReportDataTable({ dataToShow, count, columns }) {
  const styles = useStyles()

  const orderByDefault = useMemo(() => {
    const firstColumn = columns[0]
    if (!firstColumn) return null
    return firstColumn.field
  }, [columns])

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [orderBy, setOrderBy] = useState(orderByDefault)

  const rows = useMemo(() => {
    if (!dataToShow) return []

    const desc = orderBy?.startsWith('-')
    const field = desc ? orderBy?.slice(1) : orderBy

    const sorted = [...dataToShow]?.sort((a, b) => {
      const va = a[field]
      const vb = b[field]

      if (typeof va === 'number' && typeof vb === 'number') {
        return desc ? vb - va : va - vb
      }

      const vaString = String(a[field] ?? '').trim()
      const vbString = String(b[field] ?? '').trim()

      const ma = moment(vaString, dateFormats, true)
      const mb = moment(vbString, dateFormats, true)

      if (typeof va === 'string' && typeof vb === 'string' && ma.isValid() && mb.isValid()) {
        const anoNumeroA = moment(ma, 'DD/MM/YYYY HH:mm').year()
        const anoNumeroB = moment(mb, 'DD/MM/YYYY HH:mm').year()
        if (anoNumeroA !== anoNumeroB) {
          return desc ? anoNumeroB - anoNumeroA : anoNumeroA - anoNumeroB
        }

        const ta = moment(va, 'DD/MM/YYYY').valueOf()
        const tb = moment(vb, 'DD/MM/YYYY').valueOf()
        return desc ? tb - ta : ta - tb
      }

      const sa = String(va).toLowerCase()
      const sb = String(vb).toLowerCase()
      if (sa < sb) return desc ? 1 : -1
      if (sa > sb) return desc ? -1 : 1
      return 0
    })

    const start = page * pageSize
    return sorted.slice(start, start + pageSize)
  }, [dataToShow, orderBy, page, pageSize])

  const handleOrderBy = useCallback(
    orderObj => {
      const order = orderObj[0]
      if (!order) {
        handleOrderBy(orderByDefault)
        return
      }
      const { field, sort } = order
      sort === 'desc' ? setOrderBy(`-${field}`) : setOrderBy(field)
    },
    [setOrderBy]
  )

  const handleCellClick = useCallback(({ row }) => {
    if (row?.link) {
      window.open(`${row?.link}`, '_blank', 'noopener,noreferrer')
    }
  }, [])

  return (
    <Grid className={styles.mainTable}>
      <Datagrid
        initialState={{
          sorting: {
            sortModel: [{ field: orderByDefault, sort: 'asc' }],
          },
        }}
        loading={false}
        rows={rows}
        columns={columns}
        onCellClick={handleCellClick}
        rowCount={count || 0}
        sx={TABLE_SX}
        noRowsLabel="Nenhum resultado encontrado"
        paginationMode="server"
        onPageChange={setPage}
        page={page}
        onPageSizeChange={setPageSize}
        pageSize={pageSize}
        onSortModelChange={handleOrderBy}
      />
    </Grid>
  )
}
